// Noyau — TABLEAUX (lots F4 / F5) : valeurs, proportionnalité, signes, variations, statistiques, double entrée.
//
// Le tableau est un BLOC OUTIL de la feuille. Deux modes, déclarés par l'exercice (`model.table.mode`) :
//   build  l'élève crée le tableau à partir de rien (lignes, colonnes, titres, cases) : rien n'est affiché d'office ;
//   given  le tableau est fourni par l'énoncé, à compléter (structure fixe, cases données verrouillées).
// Dans les deux cas le noyau reçoit la GRILLE telle qu'elle est (lignes de cases { uid, text, kind }) et
// l'INTERPRÈTE : quelle ligne est la ligne des x, quel facteur, quelle colonne est une valeur ou un intervalle…
// Puis il juge chaque case contre ce qu'il CALCULE à partir des données de l'énoncé (jamais d'une grille-réponse) :
//   ok · unfinished (juste, calcul à finir : pas une erreur) · trap (erreur reconnue, message du pack) · wrong ·
//   pending (dépend d'une autre case pas encore juste) · unknown (ligne ou colonne non reconnue) · free (non jugée)
// et liste ce qui manque (`notes`) ; `done` quand le tableau est complet et juste.
//
// kind d'une case : 'expr' (composée dans l'éditeur), 'word' (titre choisi), 'sym' (symbole : + − 0 | ‖ ↗ ↘ −∞ +∞).
// Pédagogie (pièges, messages, mots, touches, coups de pouce) : pack « tables » (`engine.tables`).

import { num, Q, symbols } from './ast.js';
import { parse, toText, withFunctions } from './syntax.js';
import { evaluate, equivExpr } from './equiv.js';
import { atomsOf, renderTemplate, ratText, chunksOf, decText } from './pedagogy.js';
import * as SETS from './sets.js';

// Détecteurs de pièges par sorte (catalogue fermé : le pack choisit et rédige).
export const TABLE_KINDS = {
  values: ['noParens', 'powAsDouble', 'order', 'notAsked'],
  proportion: ['additive', 'inverse'],
  signs: [
    'rootSign',
    'rootOrder',
    'notRoot',
    'negativeA',
    'zeroMissing',
    'zeroExtra',
    'signRule',
    'forbidden',
  ],
  variations: [
    'vertexSign',
    'rootOrder',
    'notRoot',
    'arrowFromSign',
    'derivSign',
    'zeroMissing',
    'noParens',
    'powAsDouble',
  ],
  stats: [
    'countOff',
    'freqInverse',
    'freqOverCats',
    'percentScale',
    'notCumulative',
    'totalRule',
    'order',
    'notInData',
  ],
  cross: ['otherCell', 'difference', 'totalRule'],
  tree: ['complement', 'unconditional', 'joint', 'sumPath', 'wrongLevel'],
  sets: ['bracket', 'swapOp', 'infinityBracket', 'order', 'member', 'names'],
  pascal: ['edge', 'wrongNeighbors', 'rowIndex'],
};
export const CHOICE_ROLES = ['sign', 'arrow', 'bound', 'member'];
const DEFAULT_KEYS = {
  sign: ['+', '−', '0', '|', '‖'],
  arrow: ['↗', '↘'],
  bound: ['−∞', '+∞'],
  member: ['∈', '∉'],
};
// touches d'écriture d'un ensemble (crochets, accolades, séparateur, union, vide, infinis)
const SET_OPS = ['[', ']', ';', '{', '}', '∪', '∅', '−∞', '+∞', '−'];
const STAT_ROLES = { effectif: 'n', frequence: 'fr', pourcentage: 'pc', cumul: 'cu' };

// ── Outils numériques ─────────────────────────────────────────────────────
const roleOf = (table, ev) => Object.keys(table.nodes).find((t) => table.nodes[t].eval === ev);
const toQ = (v) => (v && typeof v === 'object' && typeof v.p === 'number' ? num(v.p, v.q) : null);
const Z = num(0),
  ONE = num(1),
  HUNDRED = num(100);
const sgn = (q) => Math.sign(q.p);
const signSym = (s) => (s > 0 ? '+' : s < 0 ? '−' : '0');
function valueOf(ast, env, table) {
  let v;
  try {
    v = evaluate(ast, env, table);
  } catch {
    return null;
  }
  return toQ(v);
}
const valueAt = (ast, x, X, table) => valueOf(ast, { [X]: x }, table);
function parseQ(text, table) {
  const v = valueOf(parse(String(text), table), {}, table);
  if (!v) throw new Error(`« ${text} » n’est pas un nombre`);
  return v;
}
const qText = (v) => ratText(v);
const norm = (s) =>
  String(s ?? '')
    .normalize('NFC')
    .replace(/\s+/g, ' ')
    .trim()
    .toLowerCase();
const eqQ = (a, b) => !!a && !!b && Q.eq(a, b);
const inQ = (v, list) => list.some((x) => eqQ(v, x));

// « fini » : Num, −Num, a/b irréductible (q > 1), −(a/b) ; loose : toute fraction d'entiers ou décimal (fréquences)
function isWrittenNumber(ast, table, loose = false) {
  const ev = (n) => table.nodes[n.t]?.eval;
  const n0 = ev(ast) === 'neg' ? ast.k[0] : ast;
  if (n0.t === 'Num') return true;
  if (ev(n0) === 'div') {
    let [a, b] = n0.k;
    if (ev(a) === 'neg') a = a.k[0];
    if (a.t !== 'Num' || b.t !== 'Num' || a.q !== 1 || b.q !== 1 || b.p <= 1) return false;
    return loose || num(a.p, b.p).q === b.p;
  }
  return false;
}
const cellHasDec = (n) => (n.t === 'Num' ? !!n.dec : (n.k || []).some(cellHasDec));
function mapAst(n, f) {
  const r = f(n);
  if (r !== undefined) return r;
  return n.k ? { ...n, k: n.k.map((c) => mapAst(c, f)) } : n;
}

// Polynôme (degré ≤ 4) par différences divisées : coefficients exacts
function polyOf(ast, X, table) {
  const xs = [0, 1, 2, 3, 4, 5].map((i) => num(i));
  const ys = xs.map((x) => valueAt(ast, x, X, table));
  if (ys.some((y) => !y)) return null;
  const n = xs.length,
    dd = ys.slice(),
    newton = [dd[0]];
  for (let j = 1; j < n; j++) {
    for (let i = n - 1; i >= j; i--)
      dd[i] = Q.div(Q.sub(dd[i], dd[i - 1]), Q.sub(xs[i], xs[i - j]));
    newton.push(dd[j]);
  }
  let c = [Z];
  for (let j = n - 1; j >= 0; j--) {
    const next = new Array(c.length + 1).fill(Z);
    c.forEach((cj, i) => {
      next[i + 1] = Q.add(next[i + 1], cj);
      next[i] = Q.sub(next[i], Q.mul(cj, xs[j]));
    });
    next[0] = Q.add(next[0], newton[j]);
    c = next;
  }
  while (c.length > 1 && Q.isZero(c[c.length - 1])) c.pop();
  const t = num(7, 3);
  const at = c.reduceRight((acc, k) => Q.add(Q.mul(acc, t), k), Z);
  const real = valueAt(ast, t, X, table);
  return real && Q.eq(at, real) ? c : null;
}
const derive = (c) => c.slice(1).map((k, i) => Q.mul(k, num(i + 1)));
function polyText(c, X) {
  const parts = [];
  for (let i = c.length - 1; i >= 0; i--) {
    const k = c[i];
    if (Q.isZero(k)) continue;
    const a = num(Math.abs(k.p), k.q);
    const mono =
      i === 0
        ? qText(a)
        : `${Q.eq(a, ONE) ? '' : qText(a)}${X}${i > 1 ? ['', '', '²', '³', '⁴'][i] : ''}`;
    parts.push(parts.length ? `${k.p < 0 ? ' − ' : ' + '}${mono}` : `${k.p < 0 ? '−' : ''}${mono}`);
  }
  return parts.join('') || '0';
}
function rationalRoots(c) {
  if (c.length === 2) return [Q.div(Q.neg(c[0]), c[1])];
  if (c.length === 3) {
    const [C, B, A] = c;
    const D = Q.sub(Q.mul(B, B), Q.mul(num(4), Q.mul(A, C)));
    if (D.p < 0) return [];
    const sq = (x) => {
      const r = Math.round(Math.sqrt(x));
      return r * r === x ? r : null;
    };
    const sp = sq(D.p),
      sqq = sq(D.q);
    if (sp == null || sqq == null) return null;
    const s = num(sp, sqq),
      den = Q.mul(num(2), A);
    const r = [Q.div(Q.sub(Q.neg(B), s), den), Q.div(Q.add(Q.neg(B), s), den)];
    return Q.eq(r[0], r[1]) ? [r[0]] : r;
  }
  return c.length <= 1 ? [] : null;
}
const sortQ = (xs) => xs.slice().sort((a, b) => Q.cmp(a, b));
const uniqQ = (xs) => xs.filter((x, i) => xs.findIndex((y) => Q.eq(x, y)) === i);

// ── Modèle : ce que le noyau calcule à partir de l'énoncé ─────────────────
export function tableModel(engine, exercise) {
  const spec = exercise.model?.table;
  if (!spec) throw new Error('cet exercice n’a pas de tableau (model.table)');
  const fnNames = Object.entries(exercise.model.symbols ?? {})
    .filter(([, v]) => v?.kind === 'function')
    .map(([n]) => n);
  if (fnNames.length) engine = { ...engine, table: withFunctions(engine.table, fnNames) };
  const kind = spec.kind;
  if (!TABLE_KINDS[kind] || kind === 'tree')
    throw new Error(
      `sorte de tableau inconnue « ${kind} » (${Object.keys(TABLE_KINDS)
        .filter((k) => k !== 'tree')
        .join(', ')})`,
    );
  const T = engine.tables || {};
  const M = {
    kind,
    spec,
    engine,
    table: engine.table,
    mode: spec.mode === 'build' ? 'build' : 'given',
    X: spec.var || 'x',
    P: T.kinds?.[kind] || {},
    keys: { ...DEFAULT_KEYS, ...(T.keys || {}) },
    vocab: T.words?.[kind] || [],
    numbers: new Set(),
    frac: false,
    parens: false,
    pow: false,
    den: false,
    words: [],
    chunks: [],
  };
  M.remember = (text) => {
    try {
      const a = atomsOf(parse(String(text), M.table), M.table);
      a.nums.forEach((n) => M.numbers.add(n));
      if (a.frac) M.frac = true;
      if (a.parens) M.parens = true;
    } catch {
      /* mot */
    }
  };
  MODEL[kind](M, spec);
  delete M.remember;
  return M;
}
const fnLabel = (spec, X, def = 'f') => spec.label || `${def}(${X})`;

const MODEL = {
  values(M, spec) {
    M.f = parse(spec.f, M.table);
    M.remember(spec.f);
    M.pow = JSON.stringify(M.f).includes(`"${roleOf(M.table, 'pow')}"`);
    M.xs = spec.xs.map((t) => {
      M.remember(t);
      const v = parseQ(t, M.table);
      if (v.p < 0 || v.q !== 1) M.parens = true;
      return v;
    });
    M.xTexts = spec.xs.map(String);
    M.fLabel = fnLabel(spec, M.X);
    M.words = [M.X, M.fLabel];
    M.chunks = chunksOf(M.engine, [M.f]);
    for (const x of M.xs) {
      const y = valueAt(M.f, x, M.X, M.table);
      if (y && y.q !== 1) M.frac = true;
    }
  },
  proportion(M, spec) {
    const [la, lb] = spec.rows;
    const cols = spec.cols.map((c) => c.map(String));
    const ref = cols.find((c) => c[0] !== '?' && c[1] !== '?');
    if (!ref)
      throw new Error('tableau de proportionnalité : il faut au moins une colonne complète');
    const A0 = parseQ(ref[0], M.table),
      B0 = parseQ(ref[1], M.table),
      k = Q.div(B0, A0);
    M.pairs = cols.map((c) => {
      c.forEach((v) => v !== '?' && M.remember(v));
      const a = c[0] === '?' ? null : parseQ(c[0], M.table),
        b = c[1] === '?' ? null : parseQ(c[1], M.table);
      if (a && b && !Q.eq(Q.mul(a, k), b))
        throw new Error(
          `tableau de proportionnalité : la colonne ${c.join(' / ')} n’est pas proportionnelle`,
        );
      return { a: a ?? Q.div(b, k), b: b ?? Q.mul(a, k), givenA: !!a, givenB: !!b, texts: c };
    });
    Object.assign(M, { k, A0, B0, la, lb, coef: !!spec.coef, refTexts: ref });
    if (k.q !== 1) M.frac = true;
    M.words = [la, lb, ...M.vocab];
  },
  signs(M, spec) {
    const X = M.X;
    const fs = [
      ...(spec.factors || []).map((t) => ({ text: t, den: false })),
      ...(spec.den || []).map((t) => ({ text: t, den: true })),
    ];
    M.factors = fs.map((F) => {
      const ast = parse(F.text, M.table);
      const c = polyOf(ast, X, M.table);
      if (!c || c.length !== 2)
        throw new Error(`tableau de signes : « ${F.text} » doit être affine (ax + b, a ≠ 0)`);
      M.remember(F.text);
      return { ...F, ast, a: c[1], b: c[0], root: Q.div(Q.neg(c[0]), c[1]) };
    });
    if (!M.factors.length) throw new Error('tableau de signes : aucun facteur');
    M.den = M.factors.some((F) => F.den);
    M.R = sortQ(uniqQ(M.factors.map((F) => F.root)));
    const wrap = (F) => `(${F.text})`;
    const numT = M.factors
        .filter((F) => !F.den)
        .map(wrap)
        .join(' × '),
      denT = M.factors
        .filter((F) => F.den)
        .map(wrap)
        .join(' × ');
    M.prodAst = parse(denT ? `(${numT}) ÷ (${denT})` : numT, M.table);
    M.fLabel = fnLabel(spec, X);
    M.words = [X, M.fLabel];
    M.chunks = chunksOf(M.engine, [M.prodAst], 10);
    for (const F of M.factors)
      if (!M.chunks.includes(F.text) && !M.chunks.includes(`(${F.text})`)) M.chunks.push(F.text);
    M.rowRoles = [...M.factors.map((F, i) => `F${i}`), 'p'];
  },
  variations(M, spec) {
    const X = M.X;
    M.f = parse(spec.f, M.table);
    const c = polyOf(M.f, X, M.table);
    if (!c || c.length < 3 || c.length > 4)
      throw new Error('tableau de variations : f doit être un polynôme de degré 2 ou 3');
    const d = derive(c),
      R = rationalRoots(d);
    if (!R)
      throw new Error(
        'tableau de variations : les racines de f′ doivent être rationnelles (choisis d’autres coefficients)',
      );
    M.remember(spec.f);
    M.pow = true;
    M.parens = true;
    M.R = sortQ(uniqQ(R));
    M.deriv = polyText(d, X);
    M.dAst = parse(M.deriv, M.table);
    M.fLabel = fnLabel(spec, X);
    M.dLabel = spec.derivLabel || `f′(${X})`;
    M.words = [X, M.dLabel, M.fLabel];
    M.chunks = chunksOf(M.engine, [M.f]);
    M.rowRoles = [...(spec.deriv === false ? [] : ['d']), 'f'];
    for (const r of M.R) {
      const y = valueAt(M.f, r, X, M.table);
      if ((y && y.q !== 1) || r.q !== 1) M.frac = true;
    }
  },
  stats(M, spec) {
    // modalités (nombres ou mots) et effectifs ; ou une série brute `data`
    let cats, counts;
    if (spec.data) {
      const vals = spec.data.map(String);
      const uniq = [...new Set(vals)];
      const numeric = uniq.every((v) => {
        try {
          parseQ(v, M.table);
          return true;
        } catch {
          return false;
        }
      });
      cats = numeric
        ? uniq.map((t) => ({ text: t, v: parseQ(t, M.table) })).sort((a, b) => Q.cmp(a.v, b.v))
        : uniq.map((t) => ({ text: t }));
      counts = cats.map(
        (c) => vals.filter((v) => (c.v ? eqQ(parseQ(v, M.table), c.v) : v === c.text)).length,
      );
    } else {
      cats = spec.values.map((t) => {
        t = String(t);
        try {
          return { text: t, v: parseQ(t, M.table) };
        } catch {
          return { text: t };
        }
      });
      counts = spec.counts.map(Number);
    }
    M.numericCats = cats.every((c) => c.v);
    if (M.numericCats) cats.forEach((c) => M.remember(c.text));
    M.cats = cats;
    M.counts = counts.map((n) => num(n));
    M.N = num(counts.reduce((a, b) => a + b, 0));
    counts.forEach((n) => M.remember(String(n)));
    M.remember(String(M.N.p));
    M.need = (spec.rows || ['effectif', 'frequence']).map((r) => STAT_ROLES[r]).filter(Boolean);
    M.total = spec.total !== false;
    M.headLabel = spec.label || 'Valeur';
    M.rowWords = {
      n: 'Effectif',
      fr: 'Fréquence',
      pc: 'Fréquence (%)',
      cu: 'Effectif cumulé',
      ...(spec.rowWords || {}),
    };
    M.words = [
      M.headLabel,
      ...Object.values(M.rowWords),
      'Total',
      ...(M.numericCats ? [] : cats.map((c) => c.text)),
      ...M.vocab,
    ];
    M.words = [...new Set(M.words)];
    if (M.need.includes('fr')) M.frac = true;
    if (M.need.includes('pc')) M.numbers.add('100');
  },
  // triangle de Pascal : lignes n = 0…N, colonnes k = 0…N ; la case (n, k) vaut C(n ; k)
  pascal(M, spec) {
    M.N = Math.max(1, Math.min(10, Number(spec.n) || 4));
    const C = (n, k) => {
      if (k < 0 || k > n) return 0;
      let c = 1;
      for (let i = 0; i < k; i++) c = (c * (n - i)) / (i + 1);
      return Math.round(c);
    };
    M.C = C;
    M.numbers.add('1');
    M.words = [];
  },
  // ensembles : intervalles / ensembles finis nommés, questions « I ∩ J », appartenances, solutions d'une relation
  sets(M, spec) {
    M.env = {};
    for (const [name, text] of Object.entries(spec.sets || {})) {
      const r = SETS.parseSet(text);
      if (r.issues.length) throw new Error(`ensemble ${name} = ${text} : ${r.issues.join(', ')}`);
      M.env[name] = r.set;
      for (const m of String(text).matchAll(/\d+(?:,\d+)?/g)) M.numbers.add(m[0]);
    }
    M.items = [];
    for (const q of spec.ask || []) {
      const ex = SETS.parseSet(q, M.env).set;
      // l'autre opération (∪ ↔ ∩) : l'erreur la plus fréquente
      const swapped = /[∪∩]/.test(q)
        ? SETS.parseSet(
            q.replace(/[∪∩]/g, (c) => (c === '∪' ? '∩' : '∪')),
            M.env,
          ).set
        : null;
      M.items.push({ label: `${q} =`, kind: 'set', set: ex, swapped, q });
    }
    for (const r of spec.solve || []) {
      M.items.push({
        label: `${r.label || 'S'} =`,
        kind: 'set',
        set: SETS.relationSet(r.rel),
        rel: r.rel,
      });
      for (const m of String(r.rel).matchAll(/\d+(?:,\d+)?/g)) M.numbers.add(m[0]);
    }
    for (const [x, name] of spec.member || []) {
      const v = SETS.parseSet(`{${x}}`).set[0].lo;
      M.items.push({
        label: `${x} … ${name}`,
        kind: 'member',
        expect: SETS.contains(M.env[name], v) ? '∈' : '∉',
        x: String(x),
        name,
        boundary: M.env[name].some(
          (p) => (p.lo !== -Infinity && Q.eq(p.lo, v)) || (p.hi !== Infinity && Q.eq(p.hi, v)),
        ),
      });
    }
    M.words = [];
  },
  cross(M, spec) {
    M.rows = spec.rows.map(String);
    M.cols = spec.cols.map(String);
    const m = spec.cells.map((r) => r.map((v) => num(Number(v))));
    const rowT = m.map((r) => r.reduce((a, b) => Q.add(a, b), Z));
    const colT = M.cols.map((_, j) => m.reduce((a, r) => Q.add(a, r[j]), Z));
    const all = rowT.reduce((a, b) => Q.add(a, b), Z);
    M.value = (ri, ci) => {
      // ri / ci : index, ou -1 pour « Total »
      if (ri < 0 && ci < 0) return all;
      if (ri < 0) return colT[ci];
      if (ci < 0) return rowT[ri];
      return m[ri][ci];
    };
    M.words = [...M.rows, ...M.cols, 'Total', ...M.vocab];
    for (const t of spec.statementNumbers || []) M.remember(String(t));
    if (!M.numbers.size) [...m.flat(), all].forEach((v) => M.numbers.add(String(v.p)));
  },
};

// ── Grille fournie (mode « given ») ───────────────────────────────────────
// → { rows: [[{ text, kind, locked }]], colKinds, locked: true } (les uid sont donnés par l'interface)
export function givenGrid(M) {
  const L = (text, kind = 'word') => ({ text: String(text), kind, locked: true });
  const E = () => ({ text: '', kind: '', locked: false });
  const line = (cells) => cells;
  if (M.kind === 'values')
    return {
      locked: true,
      rows: [
        [L(M.X), ...M.xTexts.map((t) => L(t, 'expr'))],
        [L(M.fLabel), ...M.xs.map(E)],
      ],
    };
  if (M.kind === 'proportion') {
    const rowA = [L(M.la), ...M.pairs.map((p) => (p.givenA ? L(p.texts[0], 'expr') : E()))];
    const rowB = [L(M.lb), ...M.pairs.map((p) => (p.givenB ? L(p.texts[1], 'expr') : E()))];
    const colKinds = ['label', ...M.pairs.map(() => 'value')];
    if (M.coef) {
      rowA.push(L('coefficient'));
      rowB.push(E());
      colKinds.push('coef');
    }
    return { locked: true, rows: [rowA, rowB], colKinds };
  }
  if (M.kind === 'signs' || M.kind === 'variations') {
    const colKinds = ['label', 'point'];
    M.R.forEach(() => colKinds.push('interval', 'point'));
    colKinds.push('interval', 'point');
    const n = colKinds.length;
    const xRow = [
      L(M.X, 'word'),
      L('−∞', 'sym'),
      ...Array(n - 3)
        .fill(0)
        .map((_, i) => (colKinds[i + 2] === 'point' ? E() : E())),
      L('+∞', 'sym'),
    ];
    const others = (
      M.kind === 'signs'
        ? [...M.factors.map((F) => F.text), M.fLabel]
        : [...(M.spec.deriv === false ? [] : [M.dLabel]), M.fLabel]
    ).map((lab, i) =>
      line([
        L(lab, M.kind === 'signs' && i < M.factors.length ? 'expr' : 'word'),
        ...Array(n - 1)
          .fill(0)
          .map(E),
      ]),
    );
    return { locked: true, rows: [xRow, ...others], colKinds };
  }
  if (M.kind === 'cross') {
    const show = new Set(M.spec.given || []);
    const head = [L(''), ...M.cols.map((c) => L(c)), L('Total')];
    const keys = [...M.rows, 'Total'];
    const body = keys.map((r) => [
      L(r),
      ...[...M.cols, 'Total'].map((c) => {
        const ri = r === 'Total' ? -1 : M.rows.indexOf(r),
          ci = c === 'Total' ? -1 : M.cols.indexOf(c);
        return show.has(`${r}|${c}`) ? L(qText(M.value(ri, ci)), 'expr') : E();
      }),
    ]);
    return { locked: true, rows: [head, ...body] };
  }
  if (M.kind === 'sets')
    return { locked: true, rows: M.items.map((it) => [L(it.label, 'expr'), E()]) };
  if (M.kind === 'pascal' && M.spec.tool !== false) {
    // OUTIL de consultation : le triangle déjà rempli, C(n ; k) en case
    const Z = () => ({ text: '', kind: '', locked: true, blank: true });
    const head = [L('n \\ k'), ...Array.from({ length: M.N + 1 }, (_, k) => L(String(k), 'expr'))];
    return {
      locked: true,
      pascal: true,
      tool: true,
      rows: [
        head,
        ...Array.from({ length: M.N + 1 }, (_, n) => [
          L(String(n), 'expr'),
          ...Array.from({ length: M.N + 1 }, (_, k) =>
            k <= n ? { text: String(M.C(n, k)), kind: 'expr', locked: true, pick: { n, k } } : Z(),
          ),
        ]),
      ],
    };
  }
  if (M.kind === 'pascal') {
    // triangle À REMPLIR (tool: false) : la case (n, k) n'existe que pour k ≤ n
    const Z = () => ({ text: '', kind: '', locked: true, blank: true });
    const head = [L('n \\ k'), ...Array.from({ length: M.N + 1 }, (_, k) => L(String(k), 'expr'))];
    return {
      locked: true,
      pascal: true,
      rows: [
        head,
        ...Array.from({ length: M.N + 1 }, (_, n) => [
          L(String(n), 'expr'),
          ...Array.from({ length: M.N + 1 }, (_, k) => (k <= n ? E() : Z())),
        ]),
      ],
    };
  }
  if (M.kind === 'stats') {
    const head = [
      L(M.headLabel),
      ...M.cats.map((c) => L(c.text, c.v ? 'expr' : 'word')),
      ...(M.total ? [L('Total')] : []),
    ];
    const show = new Set(M.spec.given || []);
    const rows = M.need.map((r) => [
      L(M.rowWords[r]),
      ...M.cats.map((c, j) =>
        show.has(`${r}|${c.text}`) ? L(qText(statValue(M, r, j)), 'expr') : E(),
      ),
      ...(M.total ? [E()] : []),
    ]);
    return { locked: true, rows: [head, ...rows] };
  }
  throw new Error(`pas de grille fournie pour « ${M.kind} »`);
}

// ── Lecture d'une case ────────────────────────────────────────────────────
const isEmpty = (c) => !c || !String(c.text ?? '').trim();
function cellAst(M, c) {
  if (isEmpty(c) || c.kind === 'word' || c.kind === 'sym') return null;
  try {
    return parse(String(c.text), M.table);
  } catch {
    return null;
  }
}
function cellNum(M, c) {
  const a = cellAst(M, c);
  if (!a) return null;
  if ([...symbols(a)].some((s) => M.table.syntax.constants[s] == null)) return null;
  return valueOf(a, {}, M.table);
}
function isLabel(M, c, word) {
  if (isEmpty(c)) return false;
  if (norm(c.text) === norm(word)) return true;
  const a = cellAst(M, c);
  if (!a) return false;
  try {
    return norm(toText(a, M.table)) === norm(toText(parse(word, M.table), M.table));
  } catch {
    return false;
  }
}
function sameExpr(M, c, ast) {
  const a = cellAst(M, c);
  if (!a || !ast) return false;
  try {
    return equivExpr(a, ast, M.table);
  } catch {
    return false;
  }
}

// ── Verdicts ──────────────────────────────────────────────────────────────
function say(M, tpl, facts) {
  return renderTemplate(tpl || '', facts || {});
}
function V(M, status, facts = {}, extra = {}) {
  return { status, ...extra, facts };
}
function okV(extra) {
  return { status: 'ok', ...extra };
}
function trapV(M, det, facts) {
  const t = M.P.traps?.[det];
  if (!t) return null;
  return {
    status: 'trap',
    trap: det,
    title: say(M, t.title, facts),
    message: say(M, t.why, facts),
  };
}
function wrongV(M, role, facts = {}, msg = null) {
  return {
    status: 'wrong',
    title: say(M, M.P.wrong?.title || 'Cette case n’est pas juste', facts),
    message: msg ?? say(M, M.P.wrong?.[role] || M.P.wrong?.value || '', facts),
  };
}
const note = (M, key, facts, fallback) => ({
  key,
  message: say(M, M.P.notes?.[key] || fallback, facts),
});

// Case valeur : juste, à finir, piège reconnu (valeurs fautives calculées), fausse.
function judgeValue(
  M,
  c,
  expected,
  alts = [],
  role = 'value',
  facts = {},
  loose = false,
  at = null,
) {
  if (isEmpty(c)) return null;
  if (c.kind === 'word' || c.kind === 'sym')
    return wrongV(M, role, facts, 'Cette case attend un nombre.');
  let a = cellAst(M, c);
  if (!a) return { status: 'parse_error', title: 'Je n’arrive pas à lire la case', message: '' };
  // « f(−1) = 5 » (ligne copiée de l'énoncé, x remplacé, calculée) : on lit le dernier membre ; f(a) doit viser la colonne
  if (M.table.nodes[a.t]?.relation === 'eq' || M.table.nodes[a.t]?.eval === 'eq') {
    const left = a.k[0];
    a = a.k[a.k.length - 1];
    if (left.t !== 'Num' && M.table.nodes[left.t]?.print === 'call' && at) {
      const arg = valueOf(left.k[0], {}, M.table);
      if (arg && !eqQ(arg, at))
        return wrongV(M, role, facts, `Cette case attend f(${qText(at)}), pas f(${qText(arg)}).`);
    }
    for (const m of a === left ? [] : [left]) {
      const lv = valueOf(m, {}, M.table);
      if (lv && !eqQ(lv, valueOf(a, {}, M.table)))
        return wrongV(M, role, facts, 'Les deux membres de ton égalité n’ont pas la même valeur.');
    }
  }
  if ([...symbols(a)].some((s) => M.table.syntax.constants[s] == null))
    return wrongV(
      M,
      role,
      facts,
      'Une case de tableau contient un nombre : remplace la lettre par sa valeur.',
    );
  const v = valueOf(a, {}, M.table);
  if (!v) return wrongV(M, role, facts, 'Ce calcul n’a pas de valeur.');
  if (eqQ(v, expected)) {
    if (!isWrittenNumber(a, M.table, loose))
      return {
        status: 'unfinished',
        title: say(M, M.P.unfinished?.title || 'Juste : finis le calcul', facts),
        message: say(M, M.P.unfinished?.why || 'Écris le résultat sous forme d’un nombre.', facts),
      };
    return okV({ value: v });
  }
  for (const [det, val] of alts) {
    const vs = Array.isArray(val) ? val : [val];
    if (vs.some((w) => eqQ(w, v) && !eqQ(w, expected))) {
      const t = trapV(M, det, { ...facts, answer: qText(v) });
      if (t) return t;
    }
  }
  return wrongV(M, role, { ...facts, answer: qText(v) });
}
function judgeSym(M, c, expected, family, detect = () => null, facts = {}, role = 'sign') {
  if (isEmpty(c)) return null;
  const s = String(c.text).trim();
  if (c.kind !== 'sym' || !family.includes(s))
    return wrongV(M, role, facts, `Cette case attend un des symboles : ${family.join(' ')}.`);
  if (expected.includes(s)) return okV({ symbol: s });
  return detect(s) || wrongV(M, role, facts);
}

// valeurs fautives typiques en remplaçant x par une valeur
function noParensAlt(M, f, x) {
  if (!x || x.p >= 0) return null;
  const pow = roleOf(M.table, 'pow'),
    neg = roleOf(M.table, 'neg');
  const ax = { t: 'Num', p: -x.p, q: x.q };
  const g = mapAst(f, (n) =>
    n.t === pow && n.k[0].t === 'Sym' && n.k[0].name === M.X
      ? { t: neg, k: [{ t: pow, k: [ax, n.k[1]] }] }
      : undefined,
  );
  return valueAt(g, x, M.X, M.table);
}
function powAsDoubleAlt(M, f, x) {
  const pow = roleOf(M.table, 'pow'),
    mul = roleOf(M.table, 'mul');
  const g = mapAst(f, (n) =>
    n.t === pow && n.k[1].t === 'Num' ? { t: mul, k: [n.k[1], n.k[0]] } : undefined,
  );
  return valueAt(g, x, M.X, M.table);
}

// ── Jugement d'une grille ─────────────────────────────────────────────────
// grid = { rows: [[{ uid, text, kind, locked? }]], colKinds? } → { cells: { uid → verdict }, roles, notes, done }
// Grille attendue (corrigé) d'un tableau de signes ou de variations : { rows } de cases { text, kind }, ou null.
// x : −∞, racines, +∞ ; signe sur chaque intervalle (milieu), 0 (ou ‖ pour une valeur interdite) aux racines ;
// variations : ↗ / ↘ selon le signe de f′, f(racine) aux extremums.
export function solutionGrid(M) {
  if (M.kind !== 'signs' && M.kind !== 'variations') return null;
  const X = M.X,
    R = M.R,
    T = M.table;
  const cell = (text, kind = 'sym') => ({ text, kind });
  const mid = (i) =>
    i === 0
      ? Q.add(R[0], num(-1))
      : i === R.length
        ? Q.add(R[R.length - 1], num(1))
        : Q.div(Q.add(R[i - 1], R[i]), num(2));
  const sgn = (ast, x) => {
    const v = valueAt(ast, x, X, T);
    return v ? Math.sign(v.p) : 0;
  };
  const signRow = (label, ast, zeroAt, forbid = []) => {
    const row = [cell(label, 'word'), cell('')];
    R.forEach((r, i) => {
      row.push(cell(sgn(ast, mid(i)) > 0 ? '+' : '−'));
      row.push(inQ(r, forbid) ? cell('‖') : inQ(r, zeroAt) ? cell('0') : cell(''));
    });
    row.push(cell(sgn(ast, mid(R.length)) > 0 ? '+' : '−'), cell(''));
    return row;
  };
  const xRow = [cell(X, 'word'), cell('−∞')];
  R.forEach((r) => {
    xRow.push(cell(''));
    xRow.push(cell(qText(r), 'expr'));
  });
  xRow.push(cell(''), cell('+∞'));
  if (M.kind === 'signs') {
    const forbid = M.factors.filter((F) => F.den).map((F) => F.root);
    return {
      rows: [
        xRow,
        ...M.factors.map((F) => signRow(F.text, F.ast, [F.root])),
        signRow(M.fLabel, M.prodAst, R, forbid),
      ],
    };
  }
  const dRow = signRow(M.dLabel, M.dAst, R);
  const fRow = [cell(M.fLabel, 'word'), cell('')];
  R.forEach((r, i) => {
    fRow.push(cell(sgn(M.dAst, mid(i)) > 0 ? '↗' : '↘'));
    fRow.push(cell(qText(valueAt(M.f, r, X, T)), 'expr'));
  });
  fRow.push(cell(sgn(M.dAst, mid(R.length)) > 0 ? '↗' : '↘'), cell(''));
  return { rows: [xRow, ...(M.spec.deriv === false ? [] : [dRow]), fRow] };
}

export function judgeGrid(M, grid) {
  const out = { cells: {}, notes: [], done: false, rowRoles: [] };
  const rows = (grid.rows || []).map((r) => r.map((c) => c || { text: '' }));
  out.put = (c, v) => {
    if (c?.uid && v) out.cells[c.uid] = v;
  };
  JUDGE[M.kind](M, rows, grid, out);
  delete out.put;
  // une case remplie non jugée et qui ne devait rien contenir : signalée
  const bad = Object.values(out.cells).some((v) =>
    ['wrong', 'trap', 'parse_error', 'unknown', 'unfinished'].includes(v.status),
  );
  if (bad) out.done = false;
  return out;
}
const width = (rows) => Math.max(1, ...rows.map((r) => r.length));

const JUDGE = {
  values(M, rows, grid, out) {
    const roles = rows.map((r) =>
      isLabel(M, r[0], M.X)
        ? 'x'
        : isLabel(M, r[0], M.fLabel) || sameExpr(M, r[0], M.f)
          ? 'f'
          : null,
    );
    labelVerdicts(M, rows, roles, out);
    const xi = roles.indexOf('x'),
      fi = roles.indexOf('f');
    if (xi < 0)
      out.notes.push(
        note(
          M,
          'xRow',
          { var: M.X },
          `Fais une ligne pour ${M.X} : écris « ${M.X} » dans sa première case.`,
        ),
      );
    if (fi < 0)
      out.notes.push(note(M, 'fRow', { f: M.fLabel }, `Fais une ligne pour ${M.fLabel}.`));
    const n = width(rows),
      xs = [];
    if (xi >= 0) {
      let prev = null;
      const seen = [];
      for (let c = 1; c < n; c++) {
        const cell = rows[xi][c];
        if (isEmpty(cell)) continue;
        if (cell.locked) {
          xs[c] = cellNum(M, cell);
          continue;
        }
        const v = cellNum(M, cell);
        let verdict;
        if (!v) verdict = wrongV(M, 'x', { var: M.X }, `Écris une valeur de ${M.X}.`);
        else if (!inQ(v, M.xs))
          verdict =
            trapV(M, 'notAsked', { x: qText(v), var: M.X }) ||
            wrongV(M, 'x', {}, `${M.X} = ${qText(v)} n’est pas demandé.`);
        else if (inQ(v, seen))
          verdict = wrongV(M, 'x', {}, `${qText(v)} est déjà dans le tableau.`);
        else if (prev && Q.cmp(v, prev) < 0)
          verdict =
            trapV(M, 'order', { var: M.X }) ||
            wrongV(M, 'x', {}, 'Range les valeurs dans l’ordre croissant.');
        else {
          verdict = okV({ value: v });
          xs[c] = v;
          prev = v;
        }
        seen.push(v);
        out.put(cell, verdict);
      }
      const have = xs.filter(Boolean);
      const miss = M.xs.filter((x) => !inQ(x, have)).length;
      if (miss)
        out.notes.push(
          note(
            M,
            'missingX',
            { n: miss, var: M.X },
            `Il manque ${miss} valeur${miss > 1 ? 's' : ''} de ${M.X}.`,
          ),
        );
    }
    let complete = xi >= 0 && fi >= 0 && M.xs.every((x) => xs.some((y) => eqQ(x, y)));
    if (fi >= 0)
      for (let c = 1; c < n; c++) {
        const cell = rows[fi][c],
          x = xs[c];
        if (!x) {
          if (!isEmpty(cell))
            out.put(cell, {
              status: 'pending',
              message: `Écris d’abord la valeur de ${M.X} de cette colonne.`,
            });
          continue;
        }
        const facts = { x: qText(x), f: M.spec.f, var: M.X };
        const v = judgeValue(
          M,
          cell,
          valueAt(M.f, x, M.X, M.table),
          [
            ['noParens', noParensAlt(M, M.f, x)],
            ['powAsDouble', powAsDoubleAlt(M, M.f, x)],
          ],
          'value',
          facts,
          false,
          x,
        );
        out.put(cell, v);
        if (v?.status !== 'ok') complete = false;
      }
    otherRows(M, rows, roles, out, [xi, fi]);
    out.rowRoles = roles;
    out.done = complete;
  },

  proportion(M, rows, grid, out) {
    const roles = rows.map((r) =>
      isLabel(M, r[0], M.la) ? 'a' : isLabel(M, r[0], M.lb) ? 'b' : null,
    );
    labelVerdicts(M, rows, roles, out);
    const ai = roles.indexOf('a'),
      bi = roles.indexOf('b');
    if (ai < 0 || bi < 0) {
      out.notes.push(
        note(
          M,
          'rows',
          { rowA: M.la, rowB: M.lb },
          `Fais une ligne « ${M.la} » et une ligne « ${M.lb} ».`,
        ),
      );
      out.rowRoles = roles;
      return;
    }
    const n = width(rows),
      facts = { a0: M.refTexts[0], b0: M.refTexts[1], rowA: M.la, rowB: M.lb };
    const found = new Set();
    for (let c = 1; c < n; c++) {
      const ca = rows[ai][c],
        cb = rows[bi][c];
      if (grid.colKinds?.[c] === 'coef') {
        out.put(cb, judgeValue(M, cb, M.k, [['inverse', Q.div(ONE, M.k)]], 'value', facts, false));
        if (out.cells[cb?.uid]?.status === 'ok') found.add('k');
        continue;
      }
      const a = cellNum(M, ca),
        b = cellNum(M, cb);
      // la colonne se rattache à une paire de l'énoncé par une valeur donnée (ou juste)
      const pi = M.pairs.findIndex(
        (p) => (ca?.locked && eqQ(a, p.a)) || (cb?.locked && eqQ(b, p.b)),
      );
      const p = pi >= 0 ? M.pairs[pi] : M.pairs.find((x) => eqQ(a, x.a) || eqQ(b, x.b));
      if (!p) {
        if (!isEmpty(ca) && !ca.locked)
          out.put(
            ca,
            a
              ? wrongV(M, 'value', facts, 'Cette valeur n’est pas dans l’énoncé.')
              : wrongV(M, 'value', facts, 'Cette case attend un nombre.'),
          );
        if (!isEmpty(cb) && !cb.locked)
          out.put(
            cb,
            b
              ? wrongV(M, 'value', facts, 'Cette valeur n’est pas dans l’énoncé.')
              : wrongV(M, 'value', facts, 'Cette case attend un nombre.'),
          );
        continue;
      }
      let okA = !!ca?.locked,
        okB = !!cb?.locked;
      if (!ca?.locked && !isEmpty(ca)) {
        const v = judgeValue(
          M,
          ca,
          p.a,
          [
            ['additive', b && Q.add(M.A0, Q.sub(b, M.B0))],
            ['inverse', b && Q.div(b, Q.div(ONE, M.k))],
          ],
          'value',
          facts,
        );
        out.put(ca, v);
        okA = v?.status === 'ok';
      }
      if (!cb?.locked && !isEmpty(cb)) {
        const v = judgeValue(
          M,
          cb,
          p.b,
          [
            ['additive', a && Q.add(M.B0, Q.sub(a, M.A0))],
            ['inverse', a && Q.mul(a, Q.div(ONE, M.k))],
          ],
          'value',
          facts,
        );
        out.put(cb, v);
        okB = v?.status === 'ok';
      }
      if (okA && okB) found.add(M.pairs.indexOf(p));
    }
    out.rowRoles = roles;
    out.done = M.pairs.every((_, i) => found.has(i)) && (!M.coef || found.has('k'));
  },

  signs(M, rows, grid, out) {
    lineTable(M, rows, grid, out);
  },
  variations(M, rows, grid, out) {
    lineTable(M, rows, grid, out);
  },

  stats(M, rows, grid, out) {
    if (!rows.length) return;
    // ligne d'en-tête : la première ; son titre est libre
    const head = rows[0];
    out.put(head[0], isEmpty(head[0]) ? null : { status: 'free' });
    const n = width(rows),
      colCat = [];
    let totalCol = -1,
      prev = null;
    const seen = [];
    for (let c = 1; c < n; c++) {
      const cell = head[c];
      if (isEmpty(cell)) continue;
      if (isLabel(M, cell, 'Total')) {
        if (totalCol >= 0) out.put(cell, wrongV(M, 'head', {}, 'Une seule colonne « Total ».'));
        else {
          totalCol = c;
          out.put(cell, okV());
        }
        continue;
      }
      const j = M.cats.findIndex((k) =>
        k.v ? eqQ(cellNum(M, cell), k.v) : isLabel(M, cell, k.text),
      );
      let v;
      if (j < 0)
        v =
          trapV(M, 'notInData', { value: cell.text }) ||
          wrongV(M, 'head', {}, 'Cette valeur n’apparaît pas dans la série.');
      else if (seen.includes(j))
        v = wrongV(M, 'head', {}, 'Cette valeur est déjà dans le tableau.');
      else if (totalCol >= 0) v = wrongV(M, 'head', {}, 'La colonne « Total » vient en dernier.');
      else if (M.numericCats && prev != null && Q.cmp(M.cats[j].v, M.cats[prev].v) < 0)
        v =
          trapV(M, 'order', {}) ||
          wrongV(M, 'head', {}, 'Range les valeurs dans l’ordre croissant.');
      else {
        v = okV();
        colCat[c] = j;
        prev = j;
      }
      if (j >= 0) seen.push(j);
      if (cell.locked) {
        colCat[c] = j;
        continue;
      }
      out.put(cell, v);
    }
    const missCats = M.cats.filter((_, j) => !colCat.includes(j)).length;
    if (missCats)
      out.notes.push(
        note(
          M,
          'missingCats',
          { n: missCats },
          `Il manque ${missCats} valeur${missCats > 1 ? 's' : ''} de la série dans la ligne du haut.`,
        ),
      );
    if (M.total && totalCol < 0)
      out.notes.push(note(M, 'missingTotal', {}, 'Il manque la colonne « Total ».'));
    const roles = rows.map((r, i) =>
      i === 0
        ? 'head'
        : Object.keys(M.rowWords).find((k) => isLabel(M, r[0], M.rowWords[k])) || null,
    );
    for (let i = 1; i < rows.length; i++) {
      const lab = rows[i][0];
      if (lab?.locked || isEmpty(lab)) continue;
      out.put(
        lab,
        roles[i]
          ? M.need.includes(roles[i])
            ? okV()
            : wrongV(M, 'label', {}, 'Cette ligne n’est pas demandée.')
          : wrongV(M, 'label', {}, 'Ce titre ne convient pas à une ligne de ce tableau.'),
      );
    }
    const missRows = M.need.filter((r) => !roles.includes(r));
    if (missRows.length)
      out.notes.push(
        note(
          M,
          'missingRows',
          { rows: missRows.map((r) => M.rowWords[r]) },
          `Il manque ${missRows.length > 1 ? 'des lignes' : 'une ligne'} : ${missRows.map((r) => M.rowWords[r]).join(', ')}.`,
        ),
      );
    let complete = !missCats && (!M.total || totalCol >= 0) && !missRows.length;
    const k = M.cats.length;
    for (let i = 1; i < rows.length; i++) {
      const role = roles[i];
      for (let c = 1; c < n; c++) {
        const cell = rows[i][c];
        if (!role || !M.need.includes(role)) {
          if (!isEmpty(cell) && !cell.locked)
            out.put(cell, { status: 'unknown', message: 'Donne d’abord un titre à cette ligne.' });
          continue;
        }
        const j = colCat[c],
          isTot = c === totalCol;
        if (j == null && !isTot) {
          if (!isEmpty(cell) && !cell.locked)
            out.put(cell, {
              status: 'pending',
              message: 'Écris d’abord la valeur de cette colonne, en haut.',
            });
          continue;
        }
        if (cell?.locked) continue;
        if (isEmpty(cell)) {
          if (!(role === 'cu' && isTot)) complete = false;
          continue;
        }
        const facts = { value: isTot ? 'Total' : M.cats[j].text, N: M.N.p };
        let v;
        if (isTot) {
          const exp = { n: M.N, fr: ONE, pc: HUNDRED, cu: M.N }[role];
          v = judgeValue(
            M,
            cell,
            exp,
            [['totalRule', role === 'fr' ? [M.N, HUNDRED] : role === 'pc' ? [M.N, ONE] : []]],
            'total',
            facts,
            true,
          );
        } else {
          const cnt = M.counts[j],
            exp = statValue(M, role, j);
          const alts = {
            n: [['countOff', [Q.add(cnt, ONE), Q.sub(cnt, ONE)]]],
            fr: [
              ['freqInverse', Q.div(M.N, cnt)],
              ['freqOverCats', Q.div(cnt, num(k))],
              ['percentScale', Q.mul(exp, HUNDRED)],
            ],
            pc: [
              ['percentScale', Q.div(exp, HUNDRED)],
              ['freqInverse', Q.mul(Q.div(M.N, cnt), HUNDRED)],
            ],
            cu: [['notCumulative', cnt]],
          }[role];
          v = judgeValue(M, cell, exp, alts, role, facts, role === 'fr');
        }
        out.put(cell, v);
        if (v?.status !== 'ok') complete = false;
      }
    }
    out.rowRoles = roles;
    out.done = complete;
  },

  pascal(M, rows, grid, out) {
    let complete = true;
    for (let n = 0; n <= M.N; n++)
      for (let k = 0; k <= n; k++) {
        const cell = rows[n + 1]?.[k + 1];
        if (!cell || cell.locked) continue;
        if (isEmpty(cell)) {
          complete = false;
          continue;
        }
        const facts = { n, k };
        const exp = num(M.C(n, k));
        let v;
        if (k === 0 || k === n) v = judgeValue(M, cell, exp, [], 'value', facts);
        else
          v = judgeValue(
            M,
            cell,
            exp,
            [
              [
                'wrongNeighbors',
                [num(M.C(n - 1, k) + M.C(n, k - 1)), num(M.C(n - 1, k - 1) + M.C(n, k - 1))],
              ],
              ['rowIndex', [num(M.C(n + 1, k)), num(M.C(n - 1, k))]],
            ],
            'value',
            facts,
          );
        if (v?.status === 'wrong' && (k === 0 || k === n)) v = trapV(M, 'edge', facts) || v;
        out.put(cell, v);
        if (v?.status !== 'ok') complete = false;
      }
    out.done = complete;
  },

  sets(M, rows, grid, out) {
    let complete = true;
    rows.forEach((r, i) => {
      const it = M.items[i],
        cell = r[1];
      if (!it || !cell || cell.locked) return;
      if (isEmpty(cell)) {
        complete = false;
        return;
      }
      const facts = { q: it.q || it.rel || '', x: it.x, name: it.name };
      let v;
      if (it.kind === 'member') {
        v = judgeSym(
          M,
          cell,
          [it.expect],
          M.keys.member,
          () => (it.boundary ? trapV(M, 'member', facts) : null),
          facts,
          'member',
        );
      } else {
        let r0;
        try {
          r0 = SETS.parseSet(cell.text, M.env);
        } catch (e) {
          out.put(cell, {
            status: 'parse_error',
            title: 'Je n’arrive pas à lire cet ensemble',
            message: e.message,
          });
          complete = false;
          return;
        }
        if (r0.issues.includes('infinityBracket'))
          v = trapV(M, 'infinityBracket', facts) || wrongV(M, 'set', facts);
        else if (r0.issues.includes('order'))
          v = trapV(M, 'order', facts) || wrongV(M, 'set', facts);
        else if (SETS.sameSet(r0.set, it.set))
          v = r0.issues.includes('names')
            ? {
                status: 'unfinished',
                title: say(M, M.P.unfinished?.title || 'Juste : écris l’ensemble lui-même'),
                message: say(M, M.P.unfinished?.why || ''),
              }
            : okV();
        else if (it.swapped && SETS.sameSet(r0.set, it.swapped))
          v = trapV(M, 'swapOp', facts) || wrongV(M, 'set', facts);
        else if (SETS.sameClosure(r0.set, it.set))
          v = trapV(M, 'bracket', facts) || wrongV(M, 'set', facts);
        else v = wrongV(M, 'set', { ...facts, answer: SETS.setText(r0.set) });
      }
      out.put(cell, v);
      if (v?.status !== 'ok') complete = false;
    });
    out.done = complete && rows.length >= M.items.length;
  },

  cross(M, rows, grid, out) {
    if (!rows.length) return;
    const n = width(rows),
      head = rows[0];
    if (!isEmpty(head[0]) && !head[0].locked) out.put(head[0], { status: 'free' });
    // orientation : les catégories d'un caractère en haut, celles de l'autre à gauche — dans un sens ou dans l'autre
    const has = (cell, list) => list.some((w) => isLabel(M, cell, w));
    let straight = 0,
      swapped = 0;
    for (let c = 1; c < n; c++) {
      if (has(head[c], M.cols)) straight++;
      if (has(head[c], M.rows)) swapped++;
    }
    for (let r = 1; r < rows.length; r++) {
      if (has(rows[r][0], M.rows)) straight++;
      if (has(rows[r][0], M.cols)) swapped++;
    }
    const swap = swapped > straight;
    const topList = swap ? M.rows : M.cols,
      leftList = swap ? M.cols : M.rows;
    const val = (ri, ci) => (swap ? M.value(ci, ri) : M.value(ri, ci)); // ri : ligne affichée, ci : colonne affichée
    const name = (list, k) => (k < 0 ? 'Total' : list[k]);
    const sameSide =
      'Les catégories d’un même caractère vont du même côté : toutes en haut, ou toutes à gauche.';
    const colKey = [],
      rowKey = [];
    const keyOf = (cell, list) =>
      isLabel(M, cell, 'Total') ? -1 : list.findIndex((w) => isLabel(M, cell, w));
    const seenC = new Set(),
      seenR = new Set();
    for (let c = 1; c < n; c++) {
      const cell = head[c];
      if (isEmpty(cell)) continue;
      if (has(cell, leftList)) {
        out.put(cell, wrongV(M, 'head', {}, sameSide));
        continue;
      }
      const k = keyOf(cell, topList);
      if (k < -1 || (k === -1 && !isLabel(M, cell, 'Total'))) {
        if (!cell.locked)
          out.put(
            cell,
            wrongV(M, 'head', {}, 'Ce titre ne correspond à aucune colonne de ce tableau.'),
          );
        continue;
      }
      if (seenC.has(k)) {
        out.put(cell, wrongV(M, 'head', {}, 'Ce titre est déjà en haut.'));
        continue;
      }
      seenC.add(k);
      colKey[c] = k;
      if (!cell.locked) out.put(cell, okV());
    }
    for (let r = 1; r < rows.length; r++) {
      const cell = rows[r][0];
      if (isEmpty(cell)) continue;
      if (has(cell, topList)) {
        out.put(cell, wrongV(M, 'head', {}, sameSide));
        continue;
      }
      const k = keyOf(cell, leftList);
      if (k === -1 && !isLabel(M, cell, 'Total')) {
        if (!cell.locked)
          out.put(
            cell,
            wrongV(M, 'head', {}, 'Ce titre ne correspond à aucune ligne de ce tableau.'),
          );
        continue;
      }
      if (seenR.has(k)) {
        out.put(cell, wrongV(M, 'head', {}, 'Cette ligne existe déjà.'));
        continue;
      }
      seenR.add(k);
      rowKey[r] = k;
      if (!cell.locked) out.put(cell, okV());
    }
    const needC = [...topList.map((_, i) => i), -1],
      needR = [...leftList.map((_, i) => i), -1];
    const missC = needC.filter((k) => !seenC.has(k)).length,
      missR = needR.filter((k) => !seenR.has(k)).length;
    if (missC)
      out.notes.push(
        note(
          M,
          'missingCols',
          { n: missC },
          `Il manque ${missC} colonne${missC > 1 ? 's' : ''} (titres en haut, total compris).`,
        ),
      );
    if (missR)
      out.notes.push(
        note(
          M,
          'missingRows',
          { n: missR },
          `Il manque ${missR} ligne${missR > 1 ? 's' : ''} (titres à gauche, total compris).`,
        ),
      );
    let complete = !missC && !missR;
    for (let r = 1; r < rows.length; r++)
      for (let c = 1; c < n; c++) {
        const cell = rows[r][c],
          ri = rowKey[r],
          ci = colKey[c];
        if (ri == null || ci == null) {
          if (!isEmpty(cell) && !cell?.locked)
            out.put(cell, {
              status: 'pending',
              message: 'Donne d’abord un titre à cette ligne et à cette colonne.',
            });
          continue;
        }
        if (cell?.locked) continue;
        if (isEmpty(cell)) {
          complete = false;
          continue;
        }
        const exp = val(ri, ci);
        const others = [];
        for (const k of [...topList.map((_, i) => i), -1]) if (k !== ci) others.push(val(ri, k));
        for (const k of [...leftList.map((_, i) => i), -1]) if (k !== ri) others.push(val(k, ci));
        const diffs = [];
        if (ri >= 0 && ci >= 0) {
          for (const k of topList.map((_, i) => i))
            if (k !== ci) diffs.push(Q.add(val(ri, -1), val(ri, k)));
          for (const k of leftList.map((_, i) => i))
            if (k !== ri) diffs.push(Q.add(val(-1, ci), val(k, ci)));
        }
        const facts = { row: name(leftList, ri), col: name(topList, ci) };
        const alts =
          ri < 0 || ci < 0
            ? [['totalRule', others]]
            : [
                ['difference', diffs],
                ['otherCell', others],
              ];
        const v = judgeValue(M, cell, exp, alts, 'value', facts);
        out.put(cell, v);
        if (v?.status !== 'ok') complete = false;
      }
    out.done = complete;
  },
};
function statValue(M, role, j) {
  const cnt = M.counts[j];
  if (role === 'n') return cnt;
  if (role === 'fr') return Q.div(cnt, M.N);
  if (role === 'pc') return Q.div(Q.mul(cnt, HUNDRED), M.N);
  if (role === 'cu') return M.counts.slice(0, j + 1).reduce((a, b) => Q.add(a, b), Z);
  return null;
}
function labelVerdicts(M, rows, roles, out) {
  rows.forEach((r, i) => {
    const c = r[0];
    if (isEmpty(c) || c.locked) return;
    out.put(
      c,
      roles[i]
        ? okV()
        : {
            status: 'unknown',
            title: 'Ligne non reconnue',
            message: say(
              M,
              M.P.notes?.unknownRow ||
                'Je ne reconnais pas ce titre de ligne : choisis-le parmi les titres proposés ou écris l’expression de l’énoncé.',
              {},
            ),
          },
    );
  });
}
function otherRows(M, rows, roles, out, used) {
  rows.forEach((r, i) => {
    if (used.includes(i)) return;
    for (let c = 1; c < r.length; c++)
      if (!isEmpty(r[c]) && !r[c].locked)
        out.put(r[c], {
          status: 'unknown',
          message: 'Donne d’abord un titre reconnu à cette ligne.',
        });
  });
}

// Tableaux de signes et de variations : ligne des x (−∞, valeurs, +∞), colonnes « point » et « intervalle ».
function lineTable(M, rows, grid, out) {
  const X = M.X,
    sig = M.kind === 'signs';
  const roleOfRow = (c) => {
    if (isEmpty(c)) return null;
    if (isLabel(M, c, X)) return 'x';
    if (sig) {
      if (isLabel(M, c, M.fLabel) || sameExpr(M, c, M.prodAst)) return 'p';
      const i = M.factors.findIndex((F) => sameExpr(M, c, F.ast));
      return i >= 0 ? `F${i}` : null;
    }
    if (isLabel(M, c, M.dLabel) || sameExpr(M, c, M.dAst)) return 'd';
    if (isLabel(M, c, M.fLabel) || sameExpr(M, c, M.f)) return 'f';
    return null;
  };
  const roles = rows.map((r) => roleOfRow(r[0]));
  // une ligne en double : la seconde est fausse
  roles.forEach((ro, i) => {
    if (ro && roles.indexOf(ro) !== i) roles[i] = 'dup';
  });
  rows.forEach((r, i) => {
    const c = r[0];
    if (isEmpty(c) || c.locked) return;
    out.put(
      c,
      roles[i] === 'dup'
        ? wrongV(M, 'label', {}, 'Cette ligne existe déjà.')
        : roles[i]
          ? okV()
          : {
              status: 'unknown',
              title: 'Ligne non reconnue',
              message: say(
                M,
                M.P.notes?.unknownRow || 'Je ne reconnais pas ce titre de ligne.',
                {},
              ),
            },
    );
  });
  const n = width(rows),
    xi = roles.indexOf('x');
  out.rowRoles = roles;
  if (xi < 0) {
    out.notes.push(
      note(
        M,
        'xRow',
        { var: X },
        `Commence par la ligne des ${X} : écris « ${X} » dans sa première case.`,
      ),
    );
    rows.forEach((r, i) =>
      r.forEach((c, j) => {
        if (j && !isEmpty(c) && !c.locked)
          out.put(c, { status: 'pending', message: `Fais d’abord la ligne des ${X}.` });
      }),
    );
    return;
  }
  // ── ligne des x : points
  const xr = rows[xi],
    given = !!grid.colKinds;
  const pts = []; // { c, inf: −1 | 1 | 0, v, valid }
  const fam = M.keys.bound;
  let prev = null;
  const seen = [];
  let idxR = 0;
  for (let c = 1; c < n; c++) {
    const cell = xr[c];
    if (given && grid.colKinds[c] !== 'point') {
      if (!isEmpty(cell) && !cell.locked)
        out.put(
          cell,
          wrongV(M, 'x', {}, 'Sur la ligne des x, on écrit seulement sous les traits.'),
        );
      continue;
    }
    if (given) {
      // colonnes fixées : la k-ième valeur attendue est R[k]
      if (cell?.locked) {
        pts.push({
          c,
          inf: cell.text.includes('−') || cell.text.includes('-') ? -1 : 1,
          valid: true,
        });
        continue;
      }
      const r = M.R[idxR++];
      const v = judgeXValue(M, cell, r, seen, prev);
      if (v) out.put(cell, v);
      pts.push({ c, inf: 0, v: r, valid: true, cellOk: v?.status === 'ok' });
      continue;
    }
    if (isEmpty(cell)) continue;
    if (cell.kind === 'sym' && fam.includes(String(cell.text).trim())) {
      const inf = String(cell.text).includes('+') ? 1 : -1;
      pts.push({ c, inf, valid: true });
      out.put(cell, okV());
      continue;
    }
    const v = cellNum(M, cell);
    let verdict,
      valid = false;
    if (!v) verdict = wrongV(M, 'x', { var: X }, `Écris une valeur de ${X}, ou −∞ / +∞ aux bouts.`);
    else if (inQ(v, M.R) && !inQ(v, seen) && (!prev || Q.cmp(v, prev) > 0)) {
      verdict = okV({ value: v });
      valid = true;
      prev = v;
    } else if (inQ(v, seen)) verdict = wrongV(M, 'x', {}, `${qText(v)} est déjà sur la ligne.`);
    else if (inQ(v, M.R)) verdict = trapV(M, 'rootOrder', { var: X }) || wrongV(M, 'x');
    else if (inQ(Q.neg(v), M.R))
      verdict =
        trapV(M, sig ? 'rootSign' : 'vertexSign', { var: X, fprime: M.deriv }) ||
        wrongV(M, 'x', { var: X });
    else
      verdict =
        trapV(M, 'notRoot', { var: X, x: qText(v), fprime: M.deriv }) || wrongV(M, 'x', { var: X });
    if (v) seen.push(v);
    out.put(cell, verdict);
    pts.push({ c, inf: 0, v, valid });
  }
  // bornes : −∞ d'abord, +∞ à la fin
  const first = pts[0],
    last = pts.at(-1);
  pts.forEach((p, k) => {
    if (p.inf === -1 && k !== 0) {
      out.put(xr[p.c], wrongV(M, 'x', {}, '−∞ se place au début de la ligne.'));
      p.valid = false;
    }
    if (p.inf === 1 && k !== pts.length - 1) {
      out.put(xr[p.c], wrongV(M, 'x', {}, '+∞ se place à la fin de la ligne.'));
      p.valid = false;
    }
  });
  if (!first || first.inf !== -1)
    out.notes.push(note(M, 'minusInf', {}, 'La ligne des x commence par −∞.'));
  if (!last || last.inf !== 1)
    out.notes.push(note(M, 'plusInf', {}, 'La ligne des x se termine par +∞.'));
  const have = pts.filter((p) => p.inf === 0 && p.valid).map((p) => p.v);
  const miss = M.R.filter((r) => !inQ(r, have)).length;
  if (miss && !given)
    out.notes.push(
      note(
        M,
        'missingRoots',
        { n: miss, var: X },
        `Il manque ${miss} valeur${miss > 1 ? 's' : ''} de ${X} sur la ligne.`,
      ),
    );
  // ── colonnes : point ou intervalle (échantillon entre deux points valides)
  const col = [];
  pts.forEach((p) => {
    col[p.c] = { type: 'point', ...p };
  });
  let gaps = 0;
  for (let k = 0; k + 1 < pts.length; k++) {
    const a = pts[k],
      b = pts[k + 1];
    if (b.c === a.c + 1) {
      gaps++;
      continue;
    }
    let sample = null;
    if (a.valid && b.valid) {
      if (a.inf === -1 && b.inf === 1) sample = M.R.length ? null : Z;
      else if (a.inf === -1) sample = Q.sub(b.v, ONE);
      else if (b.inf === 1) sample = Q.add(a.v, ONE);
      else sample = Q.div(Q.add(a.v, b.v), num(2));
      // aucune valeur attendue ne doit manquer entre a et b
      const lo = a.inf === -1 ? null : a.v,
        hi = b.inf === 1 ? null : b.v;
      if (M.R.some((r) => (!lo || Q.cmp(r, lo) > 0) && (!hi || Q.cmp(r, hi) < 0))) sample = null;
    }
    for (let c = a.c + 1; c < b.c; c++) col[c] = { type: 'interval', sample };
  }
  if (gaps)
    out.notes.push(
      note(
        M,
        'needInterval',
        { n: gaps },
        'Entre deux valeurs de la ligne des x, laisse une colonne pour écrire le signe (ajoute une colonne).',
      ),
    );
  for (let c = 1; c < n; c++) if (!col[c]) col[c] = { type: 'outside' };
  out.colTypes = col.map((x) => x?.type ?? null); // pour dessiner les traits sous les valeurs de x
  // ── lignes
  const need = M.rowRoles;
  const missRows = need.filter((r) => !roles.includes(r)).length;
  if (missRows)
    out.notes.push(
      note(
        M,
        'missingRows',
        { n: missRows },
        `Il manque ${missRows} ligne${missRows > 1 ? 's' : ''} dans le tableau.`,
      ),
    );
  // en mode « given », chaque valeur de la ligne des x doit aussi être écrite (et juste)
  let complete =
    !miss &&
    first?.inf === -1 &&
    last?.inf === 1 &&
    !gaps &&
    !missRows &&
    pts.every((p) => p.valid && (p.inf || !given || p.cellOk));
  const fam2 = { sign: M.keys.sign.filter((k) => k !== '‖' || M.den), arrow: M.keys.arrow };
  rows.forEach((r, i) => {
    if (i === xi) return;
    const role = roles[i];
    for (let c = 1; c < n; c++) {
      const cell = r[c],
        info = col[c];
      if (!role || role === 'dup') {
        if (!isEmpty(cell) && !cell?.locked)
          out.put(cell, {
            status: 'unknown',
            message: 'Donne d’abord un titre reconnu à cette ligne.',
          });
        continue;
      }
      const exp = expectLine(M, role, info);
      if (exp.pending) {
        if (!isEmpty(cell))
          out.put(cell, {
            status: 'pending',
            message:
              'Les valeurs de la ligne des x qui bordent cette colonne ne sont pas encore justes.',
          });
        continue;
      }
      if (exp.empty) {
        if (!isEmpty(cell) && !cell.locked)
          out.put(
            cell,
            wrongV(
              M,
              'x',
              {},
              info.type === 'point'
                ? 'Sous −∞ et +∞, on n’écrit rien ici.'
                : 'Rien à écrire dans cette colonne.',
            ),
          );
        continue;
      }
      if (isEmpty(cell)) {
        if (!exp.bar) complete = false;
        continue;
      }
      let v;
      if (exp.value) {
        const x = info.v,
          facts = { x: qText(x), f: M.spec.f, var: X };
        v = judgeValue(
          M,
          cell,
          exp.value,
          [
            ['noParens', noParensAlt(M, M.f, x)],
            ['powAsDouble', powAsDoubleAlt(M, M.f, x)],
          ],
          'value',
          facts,
          false,
          x,
        );
        if (v?.status === 'ok') v.place = exp.place;
      } else {
        const F = role[0] === 'F' ? M.factors[+role.slice(1)] : null;
        const facts = F
          ? {
              factor: F.text,
              a: qText(F.a),
              root: qText(F.root),
              var: X,
              x: info.v ? qText(info.v) : '',
            }
          : {
              f: M.fLabel,
              fprime: M.deriv,
              var: X,
              x: info.v ? qText(info.v) : '',
              sign: exp.dsign > 0 ? 'positive' : 'négative',
            };
        const famUse = exp.arrow ? fam2.arrow : fam2.sign;
        v = judgeSym(
          M,
          cell,
          exp.syms,
          famUse,
          (s) => detectLine(M, role, exp, s, F, facts),
          facts,
          exp.arrow ? 'arrow' : 'sign',
        );
      }
      out.put(cell, v);
      if (v?.status !== 'ok') complete = false;
    }
  });
  out.done = complete;
}
function judgeXValue(M, cell, r, seen, prev) {
  if (isEmpty(cell)) return null;
  const sig = M.kind === 'signs';
  const v = cellNum(M, cell);
  if (!v) return wrongV(M, 'x', { var: M.X });
  if (eqQ(v, r)) {
    const a = cellAst(M, cell);
    if (!isWrittenNumber(a, M.table))
      return {
        status: 'unfinished',
        title: say(M, M.P.unfinished?.title || 'Juste : finis le calcul'),
        message: say(M, M.P.unfinished?.why || ''),
      };
    return okV({ value: v });
  }
  if (inQ(v, M.R)) return trapV(M, 'rootOrder', { var: M.X }) || wrongV(M, 'x');
  if (inQ(Q.neg(v), M.R))
    return (
      trapV(M, sig ? 'rootSign' : 'vertexSign', { var: M.X, fprime: M.deriv }) || wrongV(M, 'x')
    );
  return (
    trapV(M, 'notRoot', { var: M.X, x: qText(v), fprime: M.deriv }) || wrongV(M, 'x', { var: M.X })
  );
}
// ce qu'attend une case selon le rôle de sa ligne et le type de sa colonne
function expectLine(M, role, info) {
  if (info.type === 'outside') return { empty: true };
  if (info.type === 'point' && info.inf) return { empty: true };
  if (info.type === 'interval' && !info.sample) return { pending: true };
  if (info.type === 'point' && !info.valid) return { pending: true };
  const X = M.X,
    t = M.table;
  if (M.kind === 'signs') {
    if (info.type === 'interval') {
      if (role === 'p')
        return {
          syms: [
            signSym(M.factors.reduce((a, F) => a * sgn(valueAt(F.ast, info.sample, X, t)), 1)),
          ],
        };
      const F = M.factors[+role.slice(1)];
      return { syms: [signSym(sgn(valueAt(F.ast, info.sample, X, t)))], F };
    }
    if (role === 'p') {
      const zD = M.factors.some((F) => F.den && eqQ(F.root, info.v)),
        zN = M.factors.some((F) => !F.den && eqQ(F.root, info.v));
      return zD
        ? { syms: ['‖'], forbidden: true }
        : zN
          ? { syms: ['0'], zero: true }
          : { syms: ['|'], bar: true };
    }
    const F = M.factors[+role.slice(1)];
    return eqQ(F.root, info.v) ? { syms: ['0'], zero: true, F } : { syms: ['|'], bar: true, F };
  }
  const ds = info.type === 'interval' ? sgn(valueAt(M.dAst, info.sample, X, t)) : 0;
  if (role === 'd')
    return info.type === 'interval'
      ? { syms: [signSym(ds)], dsign: ds }
      : { syms: ['0'], zero: true };
  if (info.type === 'interval') return { syms: [ds >= 0 ? '↗' : '↘'], arrow: true, dsign: ds };
  // extremum : valeur f(x) ; placée en haut (maximum) ou en bas (minimum)
  const k = M.R.findIndex((r) => eqQ(r, info.v));
  const before = sgn(valueAt(M.dAst, Q.sub(info.v, num(1, 1000)), X, t)),
    after = sgn(valueAt(M.dAst, Q.add(info.v, num(1, 1000)), X, t));
  return {
    value: valueAt(M.f, info.v, X, t),
    place: before > 0 && after < 0 ? 'top' : before < 0 && after > 0 ? 'bottom' : 'middle',
    k,
  };
}
function detectLine(M, role, exp, s, F, facts) {
  if (exp.zero && s !== '0') return trapV(M, 'zeroMissing', facts);
  if (exp.forbidden && s !== '‖') return trapV(M, 'forbidden', facts);
  if (exp.bar && s === '0') return trapV(M, 'zeroExtra', facts);
  if (exp.arrow) return trapV(M, 'arrowFromSign', facts);
  if (role === 'd' && (s === '+' || s === '−')) return trapV(M, 'derivSign', facts);
  if (role === 'p' && (s === '+' || s === '−')) return trapV(M, 'signRule', facts);
  if (F && F.a.p < 0 && (s === '+' || s === '−')) return trapV(M, 'negativeA', facts);
  return null;
}

// ── Touches d'une case ────────────────────────────────────────────────────
const sortNumsT = (xs) =>
  xs.sort((a, b) => parseFloat(a.replace(',', '.')) - parseFloat(b.replace(',', '.')));
// → { words: [...] , choices: [...], palette: { ids, numbers, ops, frac, parens, chunks } | null, role }
export function gridKeys(M, grid, r, c) {
  const rows = grid.rows || [];
  const judged = judgeGrid(M, grid);
  const role = judged.rowRoles?.[r] ?? null;
  // les nombres : ceux de l'énoncé et ceux déjà écrits dans le tableau (un total, une valeur calculée)
  const nums = new Set(M.numbers);
  for (const row of rows)
    for (const cell of row) {
      if (isEmpty(cell) || cell.kind !== 'expr') continue;
      const v = cellNum(M, cell),
        a = cellAst(M, cell);
      if (v && a && isWrittenNumber(a, M.table))
        nums.add((cellHasDec(a) ? (decText(v) ?? qText(v)) : qText(v)).replace(/^−/, ''));
    }
  const numVal = (t) =>
    parseFloat(t.replace(',', '.').replace(/^(\d+)\/(\d+)$/, (m, p, q) => String(p / q)));
  const value = (extra = {}) => ({
    ids: [],
    numbers: [...nums].sort((a, b) => numVal(a) - numVal(b)),
    ops: ['+', '−', '×', '÷', ...(M.pow ? ['²'] : [])],
    frac: M.frac,
    parens: M.parens,
    chunks: [],
    ...extra,
  });
  const sign = M.keys.sign.filter((k) => k !== '‖' || M.den);
  if (M.kind === 'pascal')
    return {
      role: 'value',
      words: [],
      choices: [],
      palette: { ...value(), ops: ['+'], frac: false, parens: false },
    };
  if (M.kind === 'sets') {
    const it = M.items[r];
    if (c === 0 || !it) return { role: 'label', words: [], choices: [], palette: null };
    if (it.kind === 'member')
      return { role: 'member', words: [], choices: M.keys.member, palette: null };
    return {
      role: 'set',
      words: [],
      choices: [],
      palette: {
        ids: [],
        numbers: sortNumsT([...M.numbers]),
        ops: SET_OPS,
        frac: false,
        parens: false,
        chunks: [],
      },
    };
  }
  if (c === 0) {
    // titre de ligne : mots, ou expression composée avec les morceaux de l'énoncé
    const pal = M.chunks.length
      ? { ids: [M.X], numbers: [], ops: [], frac: false, parens: false, chunks: M.chunks }
      : null;
    return { role: 'label', words: M.words, choices: [], palette: pal };
  }
  if (M.kind === 'signs' || M.kind === 'variations') {
    if (role === 'x') return { role: 'x', words: [], choices: M.keys.bound, palette: value() };
    // ligne de f : flèches, et pour un extremum f(a) l'expression de f (morceaux), x à remplacer, les puissances
    if (role === 'f' && M.kind === 'variations')
      return {
        role,
        words: [],
        choices: M.keys.arrow,
        palette: value({
          ids: [M.X],
          chunks: [toText(M.f, M.table), ...M.chunks.filter((c) => c !== toText(M.f, M.table))],
          ops: ['+', '−', '×', '÷', '²', '³', '^'],
          parens: true,
        }),
      };
    if (role) return { role, words: [], choices: sign, palette: null };
    return {
      role: null,
      words: [],
      choices: [...sign, ...(M.kind === 'variations' ? M.keys.arrow : [])],
      palette: value(),
    };
  }
  if ((M.kind === 'stats' || M.kind === 'cross') && r === 0) {
    const words =
      M.kind === 'stats' ? ['Total', ...(M.numericCats ? [] : M.cats.map((k) => k.text))] : M.words;
    return {
      role: 'head',
      words,
      choices: [],
      palette: M.kind === 'stats' && M.numericCats ? value() : null,
    };
  }
  if (M.kind === 'cross' && c === 0)
    return { role: 'head', words: M.words, choices: [], palette: null };
  return { role: role || 'value', words: [], choices: [], palette: value() };
}

// Coup de pouce d'une case : la méthode (gabarit du pack), jamais la valeur.
export function gridHint(M, grid, r, c) {
  const judged = judgeGrid(M, grid);
  const role = c === 0 ? 'label' : (judged.rowRoles?.[r] ?? null);
  const key = role ? (role[0] === 'F' ? 'F' : role) : null;
  const tpl = (key && M.P.hints?.[key]) || M.P.hint;
  const facts = {
    var: M.X,
    f: M.spec.f,
    fprime: M.deriv,
    factors: M.factors?.map((F) => F.text),
    a0: M.refTexts?.[0],
    b0: M.refTexts?.[1],
    N: M.N?.p,
  };
  if (key === 'F') {
    const F = M.factors[+role.slice(1)];
    Object.assign(facts, { factor: F.text, a: qText(F.a), root: qText(F.root) });
  }
  return tpl ? renderTemplate(tpl, facts) : null;
}

// Outil « Remplacer » : valeurs proposées pour une lettre — les valeurs de la ligne des x (écrites ou données) et
// celles de l'énoncé ; jamais seulement la bonne (l'élève choisit celle de sa colonne).
export function substituteChoices(M, grid, name) {
  if (!M || name !== M.X) return [];
  const vals = [...(M.xs || [])];
  for (const row of grid?.rows || []) {
    if (!isLabel(M, row[0], M.X)) continue;
    for (const c of row.slice(1)) {
      const v = cellNum(M, c);
      if (v && !inQ(v, vals)) vals.push(v);
    }
  }
  return sortQ(uniqQ(vals)).map(qText);
}
